import { isTenantlessPlatformUser } from "./platformAccess";
import {
  hasRequiredSessionFeatures,
  requiredHrPayrollEntitlements,
} from "./hrPayrollEntitlements";

export const PLATFORM_ROLES = Object.freeze([
  "platform_super_admin", "platform_finance", "platform_ops", "platform_support",
]);

// Platform-console authority requires an EXPLICITLY tenantless (tenant_id === null) platform account.
// Delegating to the shared platformAccess check keeps this fail-closed — a nominal platform role with a
// missing/undefined tenant_id, or a tenant-scoped platform label, is NOT treated as console authority.
export function isPlatformUser(user) {
  return isTenantlessPlatformUser(user);
}

export function canManagePlatformAutomation(user) {
  if (!isPlatformUser(user)) return false;
  const roles = user.roles || [];
  return roles.includes("platform_super_admin") || roles.includes("platform_ops");
}

// Per-persona first-load: land each role in the workspace that matches their daily job instead of a
// one-size-fits-all dashboard. The deep-research pass (2026-07) found NO verified external pattern for
// role-tailored landing, so this is a deliberately CONSERVATIVE product heuristic:
//   • non-tenant platform roles       → /platform
//   • full-access admins / leadership → /app/dashboard (the overview already adapts to them)
//   • group admin                     → /app/group
//   • narrow specialists              → their primary module (accountant→accounting, teacher→attendance, …)
// It always falls back to /app/dashboard when unsure, so it can never strand a user, and it leaves the
// demo owner (tenant_admin, *:*) on the dashboard — keeping the e2e/a11y login flows byte-identical.
export function resolveHomeRoute(user) {
  if (!user) return "/login";
  const roles = user.roles || [];
  if (isPlatformUser(user)) return "/platform";

  const perms = user.permissions || [];
  const has = (p) => {
    if (perms.includes("*:*")) return true;
    const [module, action] = p.split(":");
    const denied = Array.isArray(user.denied_capabilities)
      ? user.denied_capabilities
      : [];
    if (
      denied.includes(p)
      || denied.includes(`${module}:*`)
      || denied.includes(`*:${action}`)
      || denied.includes("*:*")
    ) return false;
    return (
      perms.includes(p)
      || perms.includes(`${module}:*`)
      || perms.includes(`*:${action}`)
    );
  };
  const canLand = (route, permission) => (
    has(permission)
    && hasRequiredSessionFeatures(user, requiredHrPayrollEntitlements(route))
  );

  // Full-access admins & school leadership keep the role-adaptive overview dashboard.
  if (perms.includes("*:*") || roles.includes("tenant_admin")) return "/app/dashboard";
  // A custom/narrow principal account must not be sent to an ungranted overview. Standard
  // principals still carry dashboard:read and retain the leadership landing page.
  if (roles.includes("principal") && has("dashboard:read")) return "/app/dashboard";
  if (roles.includes("group_admin") && has("group:read")) return "/app/group";

  // Narrow specialists land directly in the workspace they live in all day.
  if (canLand("/app/fees", "fees:read")) return "/app/fees";
  if (canLand("/app/accounting", "accounting:read")) return "/app/accounting";
  if (canLand("/app/scholarship", "scholarship:read")) return "/app/scholarship";
  if (
    canLand("/app/attendance", "attendance:read")
    || canLand("/app/attendance", "timetable:read")
    || canLand("/app/attendance", "homework:read")
  ) return "/app/attendance";
  if (canLand("/app/hr/workforce-time", "workforce_time:read")) return "/app/hr/workforce-time";
  if (canLand("/app/hr/workforce-schedules", "workforce_scheduling:read")) return "/app/hr/workforce-schedules";
  if (canLand("/app/hr/substitute-coverage", "substitute_coverage:read")) return "/app/hr/substitute-coverage";
  if (canLand("/app/hr/workforce-workload", "workforce_workload:read")) return "/app/hr/workforce-workload";
  if (canLand("/app/hr/recruitment", "recruitment:read")) return "/app/hr/recruitment";
  if (canLand("/app/hr/onboarding", "hr_onboarding:read")) return "/app/hr/onboarding";
  if (canLand("/app/hr/credentials", "credentials:read")) return "/app/hr/credentials";
  if (canLand("/app/hr/employee-finance", "employee_finance:read")) return "/app/hr/employee-finance";
  if (canLand("/app/hr/offboarding", "offboarding:read")) return "/app/hr/offboarding";
  if (canLand("/app/hr", "hr:read")) return "/app/hr";
  if (canLand("/app/payroll", "payroll:read")) return "/app/payroll";
  if (canLand("/app/admissions", "admissions:read")) return "/app/admissions";
  return "/app/dashboard";
}
